import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ContatoExistente {
  nome: string;
  telefone: string;
  cargo: string;
  email: string;
  fonte: 'leads_carteira' | 'cliente_contato';
}

/**
 * Hook centralizado para buscar contato existente por CNPJ
 * 
 * Hierarquia de busca (Regra Master de Contato):
 * 1. leads_carteira com contato_real_encontrado = true (mais recente)
 * 2. cliente_contato com is_principal = true
 * 3. cliente_contato qualquer (se não houver principal)
 */
export function useContatoExistente() {
  const [loading, setLoading] = useState(false);

  const normalizarCNPJ = (cnpj: string): string => {
    return cnpj.replace(/\D/g, '');
  };

  const buscarContatoPorCNPJ = async (cnpj: string): Promise<ContatoExistente | null> => {
    const cnpjLimpo = normalizarCNPJ(cnpj);
    
    if (cnpjLimpo.length !== 14) {
      return null;
    }

    setLoading(true);

    try {
      // Prioridade 1: leads_carteira com contato validado
      // Busca até 5 registros para montar o contato com o campo mais recente não-nulo de cada atributo
      // Email SOMENTE do contato real (leads_carteira) — nunca busca em cliente_contato para preencher email
      const { data: leadContatos, error: leadError } = await supabase
        .from('leads_carteira')
        .select('contato_real_nome, contato_real_telefone, contato_real_cargo, contato_real_email')
        .eq('cnpj', cnpjLimpo)
        .eq('contato_real_encontrado', true)
        .not('contato_real_nome', 'is', null)
        .order('data_contato_encontrado', { ascending: false, nullsFirst: false })
        .limit(5);

      if (!leadError && leadContatos && leadContatos.length > 0) {
        const nome     = leadContatos.find(l => l.contato_real_nome)?.contato_real_nome || '';
        const telefone = leadContatos.find(l => l.contato_real_telefone)?.contato_real_telefone || '';
        const cargo    = leadContatos.find(l => l.contato_real_cargo)?.contato_real_cargo || '';
        let   email    = leadContatos.find(l => l.contato_real_email)?.contato_real_email || '';

        // Se email ficou vazio, busca em outros registros do mesmo CNPJ em leads_carteira
        // (sem o filtro contato_real_encontrado=true) — ainda dentro da mesma tabela, regra preservada
        if (nome && !email) {
          const { data: emailFallback } = await supabase
            .from('leads_carteira')
            .select('contato_real_email')
            .eq('cnpj', cnpjLimpo)
            .not('contato_real_email', 'is', null)
            .order('data_contato_encontrado', { ascending: false, nullsFirst: false })
            .limit(1)
            .maybeSingle();

          email = emailFallback?.contato_real_email || '';
        }

        if (nome) {
          return { nome, telefone, cargo, email, fonte: 'leads_carteira' };
        }
      }

      // Prioridade 2 e 3: cliente_contato (principal primeiro, depois qualquer)
      const { data: cliente } = await supabase
        .from('clientes')
        .select('id')
        .eq('cnpj', cnpjLimpo)
        .maybeSingle();

      if (cliente?.id) {
        // Primeiro tenta buscar contato principal
        const { data: contatoPrincipal } = await supabase
          .from('cliente_contato')
          .select('nome, telefone, cargo, email')
          .eq('cliente_id', cliente.id)
          .eq('is_principal', true)
          .not('nome', 'is', null)
          .limit(1)
          .maybeSingle();

        if (contatoPrincipal && contatoPrincipal.nome) {
          return {
            nome: contatoPrincipal.nome,
            telefone: contatoPrincipal.telefone || '',
            cargo: contatoPrincipal.cargo || '',
            email: contatoPrincipal.email || '',
            fonte: 'cliente_contato'
          };
        }

        // Fallback: qualquer contato do cliente
        const { data: contatoQualquer } = await supabase
          .from('cliente_contato')
          .select('nome, telefone, cargo, email')
          .eq('cliente_id', cliente.id)
          .not('nome', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (contatoQualquer && contatoQualquer.nome) {
          return {
            nome: contatoQualquer.nome,
            telefone: contatoQualquer.telefone || '',
            cargo: contatoQualquer.cargo || '',
            email: contatoQualquer.email || '',
            fonte: 'cliente_contato'
          };
        }
      }

      return null;
    } catch (error) {
      console.error('Erro ao buscar contato existente:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { buscarContatoPorCNPJ, loading };
}
